import React, { DragEvent, useEffect, useState } from "react";
import styles from "./header-container.styles.css";
import headerStyles from "../table-headers.styles.css";
import { m } from "kremling";
import { ColumnId, TableSchema } from "../types";
import { Selection } from "../hooks/use-table-selection.hook";
import {
  calculateRightStickyPosition,
  isColumnBeforeRightSticky,
} from "../cp-data-table.helper";

type HeaderContainerProps = {
  filterControl: any;
  HeaderComponent: React.ComponentType<any>;
  columnSchema: any;
  columnWidth: number;
  draggable: boolean;
  selection?: Selection;
  sticky: boolean;
  isDragTarget: boolean;
  dragStyle: string;
  onColumnWidthReset: (columnId: ColumnId) => void;
  onDragStart: (e: DragEvent<HTMLTableCellElement>, columnId: ColumnId) => void;
  onDragOver: (e: DragEvent<HTMLTableCellElement>, columnId: ColumnId) => void;
  onDrop: (e: DragEvent<HTMLTableCellElement>) => void;
  onDragEnd: (e: DragEvent<HTMLTableCellElement>) => void;
  onDrag: (e: DragEvent<HTMLTableCellElement>) => void;
  onResizeStart: (
    columnId: ColumnId,
    screenX: number,
    columnWidth: number,
  ) => void;
  onResize: (e: MouseEvent) => void;
  onResizeEnd: () => void;
  canChangeWidth: boolean;
  aggregatedWidth: number;
  orderedColumns: ColumnId[];
  schema: TableSchema;
  columnWidths: Record<string, number | string>;
};

function HeaderContainerBase({
  filterControl,
  HeaderComponent,
  columnSchema,
  columnWidth,
  draggable,
  selection,
  sticky,
  isDragTarget,
  dragStyle,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  onDrag,
  onResizeStart,
  onResize,
  onResizeEnd,
  onColumnWidthReset,
  canChangeWidth,
  aggregatedWidth,
  orderedColumns,
  schema,
  columnWidths,
}: HeaderContainerProps) {
  const columnId = columnSchema.id;
  const [isResizing, setIsResizing] = useState(false);

  useEffect(() => {
    if (isResizing) {
      const handleMouseMove = (e: MouseEvent) => {
        onResize(e);
      };

      const handleMouseUp = () => {
        onResizeEnd();
        setIsResizing(false);
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isResizing, onResize, onResizeEnd]);

  function isFilterApplied() {
    if (!filterControl) return false;
    const filter = filterControl.filters[columnSchema.id];
    const sort = filterControl.sortData[columnSchema.id];
    return !!filter || !!sort;
  }

  const canDrag = draggable && !sticky;
  const isRightSticky = columnSchema.sticky === "right";
  const isLeftSticky =
    columnSchema.sticky === "left" || columnSchema.sticky === true;
  const isBeforeRightSticky = isColumnBeforeRightSticky(
    columnId,
    orderedColumns,
    schema,
  );

  let stickyStyle: React.CSSProperties = {
    width: `${columnWidth / 10}rem`,
  };

  if (isLeftSticky) {
    stickyStyle = {
      ...stickyStyle,
      left: `${(aggregatedWidth - columnWidth) / 10}rem`,
    };
  } else if (isRightSticky) {
    const rightOffset = calculateRightStickyPosition(
      columnId,
      orderedColumns,
      schema,
      columnWidths,
    );
    stickyStyle = {
      ...stickyStyle,
      right: `${rightOffset / 10}rem`,
    };
  }

  return (
    <th
      style={stickyStyle}
      draggable={canDrag}
      className={m(styles.draggable, canDrag)
        .m(styles.stickyHeader, !!columnSchema.sticky)
        .m(headerStyles.rightStickyCell, isRightSticky)
        .m(dragStyle, isDragTarget)
        .m(styles.nonStickyColumn, !sticky)
        .m(styles.filtersApplied, isFilterApplied())
        .m(styles.forceVerticalBorder, columnSchema.showVerticalBorder === true)
        .m(
          styles.forceNoVerticalBorder,
          columnSchema.showVerticalBorder === false || isBeforeRightSticky,
        )}
      onDrag={onDrag}
      onDragStart={(e) => {
        if (sticky) return;
        e.dataTransfer.effectAllowed = "move";
        onDragStart(e, columnSchema.id);
      }}
      onDragOver={(e) => {
        e.preventDefault();
        if (!sticky && !isDragTarget) {
          onDragOver(e, columnSchema.id);
        }
      }}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
    >
      <div
        className={`flex items-center ${
          columnSchema.header?.props?.align === "right" ? "justify-end" : ""
        }`}
      >
        <HeaderComponent
          columnSchema={columnSchema}
          filterControl={filterControl}
          selection={selection}
        />
      </div>
      {canChangeWidth && (
        <div
          className={
            columnSchema.sticky === "right"
              ? styles.resizeHandleRight
              : styles.resizeHandle
          }
          onMouseDown={(e) => {
            e.preventDefault();
            setIsResizing(true);
            onResizeStart(columnId, e.screenX, columnWidth);
          }}
          onDoubleClick={() => onColumnWidthReset(columnId)}
        />
      )}
    </th>
  );
}

export const HeaderContainer = React.memo(HeaderContainerBase);
